import React, { useEffect, useState } from "react";
import { MessageSquare, Phone, X, Send } from "lucide-react";
import { COMPANY } from "../mock";
import { trackEvent, trackPhoneClick } from "../lib/analytics";

/**
 * MobileClickToChat — a floating action button for mobile visitors.
 *
 * Why: local-service SEO drives huge mobile traffic, and mobile visitors
 * convert ~3x higher when they can text/call in one tap. This component
 * shows a bottom-right FAB on mobile that expands into a 3-action tray:
 *   1. Text us (opens native SMS composer with a pre-filled message)
 *   2. Call now (tel: link, fires trackPhoneClick)
 *   3. Send booking form request (scrolls to page's embedded lead form)
 *
 * The button hides on desktop (lg+) to avoid clashing with our existing
 * desktop "Call now" header CTA. It also hides on the /contact page (form
 * is already the primary content) and inside the admin route.
 */
const SMS_PREFILL = encodeURIComponent(
  "Hi Decatur Solution Works — I need help with an appliance repair. My name is:"
);

const MobileClickToChat = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Hide on the /contact and /admin routes at mount + on client-side
    // navigations (RouteTracker updates location).
    const check = () => {
      const p = window.location.pathname;
      const hide = p.startsWith("/admin") || p === "/contact";
      setVisible(!hide);
    };
    check();
    window.addEventListener("popstate", check);
    // Listen to pushState too (React Router uses it)
    const _push = window.history.pushState;
    window.history.pushState = function () {
      _push.apply(this, arguments);
      check();
    };
    return () => {
      window.removeEventListener("popstate", check);
      window.history.pushState = _push;
    };
  }, []);

  if (!visible) return null;

  const handleSMS = () => {
    trackEvent("click_to_sms", { source: "mobile_fab" });
    // sms: URI with body param is supported across iOS & Android
    window.location.href = `sms:${COMPANY.phoneRaw}?body=${SMS_PREFILL}`;
    setOpen(false);
  };

  const handleCall = () => {
    trackPhoneClick("mobile-chat-fab");
    window.location.href = `tel:${COMPANY.phoneRaw}`;
    setOpen(false);
  };

  const handleBook = () => {
    trackEvent("click_to_book_scroll", { source: "mobile_fab" });
    const target =
      document.getElementById("book") ||
      document.querySelector("[data-testid$='-form']");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "/contact";
    }
    setOpen(false);
  };

  return (
    <div
      className="lg:hidden fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3"
      data-testid="mobile-chat-fab"
    >
      {/* Expanded tray */}
      {open && (
        <div
          className="flex flex-col gap-2 items-end animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="mobile-chat-tray"
        >
          <button
            type="button"
            onClick={handleSMS}
            data-testid="mobile-chat-sms-btn"
            className="flex items-center gap-2 px-4 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-xl shadow-emerald-600/30 active:scale-95 transition-all"
          >
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            Text us
          </button>
          <button
            type="button"
            onClick={handleCall}
            data-testid="mobile-chat-call-btn"
            className="flex items-center gap-2 px-4 py-3 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-bold text-sm shadow-xl shadow-orange-700/30 active:scale-95 transition-all"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            Call {COMPANY.phone}
          </button>
          <button
            type="button"
            onClick={handleBook}
            data-testid="mobile-chat-book-btn"
            className="flex items-center gap-2 px-4 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm shadow-xl shadow-slate-900/30 active:scale-95 transition-all"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            Book a repair
          </button>
        </div>
      )}

      {/* Main FAB toggle */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) trackEvent("mobile_chat_open", { source: "mobile_fab" });
        }}
        aria-label={open ? "Close chat menu" : "Chat with Decatur Solution Works"}
        aria-expanded={open}
        data-testid="mobile-chat-toggle"
        className={`h-14 w-14 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-[transform,background-color] ${
          open
            ? "bg-slate-900 text-white"
            : "bg-orange-700 text-white ring-4 ring-orange-700/25 animate-pulse"
        }`}
      >
        {open ? (
          <X className="h-6 w-6" aria-hidden="true" strokeWidth={2.5} />
        ) : (
          <MessageSquare className="h-6 w-6" aria-hidden="true" strokeWidth={2.2} />
        )}
      </button>
    </div>
  );
};

export default MobileClickToChat;
